import {
  ContactRiskRuleName,
  WarningRuleLevel,
  WarningRuleLevels,
  WarningRuleName
} from "api/landlord/warnings-report/types";

export const WARNING_RULE_LEVELS: WarningRuleLevel[] = ["fail", "warn", "hide"];

export const WARNING_RULE_LEVEL_LABELS: Record<WarningRuleLevel, string> = {
  fail: "Fail",
  warn: "Warn",
  hide: "Hide"
};

/** [name, title when triggered, what is checked, title when passed, recommended level] */
type RuleTuple = [WarningRuleName, string, string, string, WarningRuleLevel];

interface WarningRuleDefinition {
  name: WarningRuleName;
  title: string;
  description: string;
  passedTitle: string;
  recommended: WarningRuleLevel;
}

interface WarningRuleGroup {
  key: "contact" | "identity" | "income" | "applications";
  title: string;
  description: string;
  isNewWithPremium: boolean;
  rules: WarningRuleDefinition[];
}

const rule = ([
  name,
  title,
  description,
  passedTitle,
  recommended
]: RuleTuple) => ({
  name,
  title,
  description,
  passedTitle,
  recommended
});

export const WARNING_RULE_GROUPS: WarningRuleGroup[] = [
  // P0 ships only the checks the warnings engine evaluates today; the network
  // group returns with its own phase.
  {
    key: "contact",
    title: "Email and phone risk",
    description:
      "Checked with IPQualityScore as soon as the application is submitted.",
    isNewWithPremium: true,
    // prettier-ignore
    rules: (
      [
        ["high_risk_contact", "High-risk email or phone", "Fraud score of 85 or more out of 100", "Email and phone risk is low", "fail"],
        ["disposable_email", "Disposable email address", "A temporary inbox that stops working after a short time", "Email is not disposable", "warn"],
        ["unverifiable_contact", "Email or phone can’t be verified", "Email isn’t deliverable or the phone isn’t an active line", "Email and phone are valid", "warn"],
        ["recent_contact_abuse", "Recent abuse linked to email or phone", "Tied to recent chargebacks, fraud or spam", "No recent abuse on email or phone", "warn"]
      ] as RuleTuple[]
    ).map(rule)
  },
  {
    key: "identity",
    title: "Identity and credit",
    description: "Uses identity verification and the credit report.",
    isNewWithPremium: false,
    // prettier-ignore
    rules: (
      [
        ["ssn_mismatch", "SSN does not match credit bureau", "Checked against the credit report", "SSN matches credit bureau", "fail"],
        ["recent_credit_history", "Credit file was established recently", "Credit file is less than 2 years old", "Credit file age is consistent", "warn"],
        ["partner_property", "Applicant has lived at one of your properties", "An address from our reports matches one of your properties", "Applicant hasn’t lived at your properties", "warn"],
        ["housing_history_mismatch", "Housing history is incomplete", "A credit report address is missing from the self-reported housing history", "Housing history matches credit report", "warn"]
      ] as RuleTuple[]
    ).map(rule)
  },
  {
    key: "income",
    title: "Income",
    description:
      "Uses the income documents and bank data the applicant shared.",
    isNewWithPremium: false,
    // prettier-ignore
    rules: (
      [
        ["income_discrepancy", "Verified income does not match self-reported income", "Verified and self-reported income differ by more than your threshold", "Verified income matches self-reported income", "warn"],
        ["income_fraud_signal", "Income documents show fraud signals", "A document looks edited or generated", "No fraud signals in income documents", "fail"],
        ["income_name_mismatch", "Name mismatch in the income documents", "The name on a document doesn’t match the applicant", "Names on income documents match", "warn"],
        ["duplicate_income", "Duplicate income found", "The same income source was selected by multiple applicants", "No duplicate income", "warn"]
      ] as RuleTuple[]
    ).map(rule)
  },
  {
    key: "applications",
    title: "Applications with you",
    description: "Uses earlier applications submitted to you.",
    isNewWithPremium: false,
    // prettier-ignore
    rules: (
      [
        ["previous_applications", "Applicant applied with you before", "This person submitted an earlier application with you", "No previous applications", "warn"]
      ] as RuleTuple[]
    ).map(rule)
  }
];

export const CONTACT_RISK_RULE_NAMES: ContactRiskRuleName[] = [
  "high_risk_contact",
  "disposable_email",
  "unverifiable_contact",
  "recent_contact_abuse"
];

const WARNING_RULES = WARNING_RULE_GROUPS.flatMap((g) => g.rules);

export const WARNING_RULES_BY_NAME = Object.fromEntries(
  WARNING_RULES.map((r) => [r.name, r])
) as Record<WarningRuleName, WarningRuleDefinition>;

export const RECOMMENDED_WARNING_RULE_LEVELS: WarningRuleLevels =
  Object.fromEntries(WARNING_RULES.map((r) => [r.name, r.recommended]));

export const getWarningRuleLevel = (
  levels: WarningRuleLevels | undefined,
  name: WarningRuleName
): WarningRuleLevel =>
  levels?.[name] ?? RECOMMENDED_WARNING_RULE_LEVELS[name] ?? "warn";

export const countWarningRuleLevels = (
  levels: WarningRuleLevels | undefined
): Record<WarningRuleLevel, number> => {
  const counts: Record<WarningRuleLevel, number> = {
    fail: 0,
    warn: 0,
    hide: 0
  };
  WARNING_RULES.forEach((r) => {
    counts[getWarningRuleLevel(levels, r.name)] += 1;
  });
  return counts;
};
